import { Filter } from "@mui/icons-material";
import { Alert, Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { FC, useState } from "react";
import { Dialog } from "~/components/dialog";
import { $dayjs, DATEYEARMONTH_FORMAT } from "~/libs/dayjs";
import { GetFileByIdResults } from "~/queries/useGetFileById";
import { PAYMENT_VERIFICATION_UPLOAD_STATUS } from "~/types/__generated/gql/graphql";
interface FileAttachment {
  file: File;
  preview: string | null;
  type: string;
}
export const PaymentVerificationDialog: FC<{
  file: GetFileByIdResults;
  uploadFiles: FileAttachment[] | undefined;
  handleConfirm: (paymentUploadStatus: PAYMENT_VERIFICATION_UPLOAD_STATUS, receivedAmount: number, receivedDate?: Date) => Promise<void>;
  handleClose: () => void;
}> = ({
  file,
  uploadFiles,
  handleConfirm,
  handleClose
}) => {
  const [receivedDate, setReceivedDate] = useState<Date | null>(null);
  const [receivedAmount, setReceivedAmount] = useState<number | undefined>();
  const [paymentUploadStatus, setPaymentUploadStatus] = useState<PAYMENT_VERIFICATION_UPLOAD_STATUS | null>(null);
  if (!file) return;
  return <Dialog dialogProps={{
    maxWidth: "sm"
  }} title={`Upload Payment Verification`} content={<>
					<Box sx={{
      display: "flex",
      mb: 2
    }}>
						<Alert severity="info" sx={{
        width: "100%"
      }}>
							You are about to upload below files into payment verification.
						</Alert>
					</Box>
					<Box sx={{
      display: "flex",
      flexDirection: "column",
      mb: 2
    }}>
						<Typography>
							<strong>Order #: </strong>
							{`${file.counterConfigs?.file?._counter.isPredefinedFormat ? `${$dayjs(file.createdAt).format(DATEYEARMONTH_FORMAT)}` : file.counterConfigs?.file?._counter.prefix}-${file.counterConfigs?.file?.index ? String(file.counterConfigs.file?.index || 0).padStart(file.counterConfigs.file?._counter?.suffix_digit || 0, "0") : ""}${file.fileSuffixComment ? `-${file.fileSuffixComment}` : ""}`}
						</Typography>
						<Typography>
							<strong>
								{file._receipt ? file._receipt.isFullyPaid ? "Receipt " : "Invoice " : "Invoice "}
								#:{" "}
							</strong>
							{`${file.counterConfigs?.invoice?._counter.isPredefinedFormat ? `${$dayjs(file.createdAt).format(DATEYEARMONTH_FORMAT)}` : file.counterConfigs?.invoice?._counter.prefix}-${file.counterConfigs?.invoice?.index ? String(file.counterConfigs.invoice?.index || 0).padStart(file.counterConfigs.invoice?._counter?.suffix_digit || 0, "0") : ""}${file.receiptSuffixComment ? `-${file.receiptSuffixComment}` : ""}`}
						</Typography>
					</Box>
					{(uploadFiles ?? []).map((a, i) => <Grid key={`payment-verfication-upload-${i}`} alignContent="center" alignItems="center" container>
							<Grid xs={12}>
								<Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          border: "1px solid #ccc",
          borderRadius: 2,
          p: 1,
          mt: 2
        }}>
									<Filter fontSize="large" />
									<Box sx={{
            flexGrow: 1
          }}>
										<Typography variant="body2">
											{a.file.name.length > 40 ? `${a.file.name.substring(0, 20)}.....${a.file.name.substring(a.file.name.length - 20)}` : a.file.name}
										</Typography>
									</Box>
								</Box>
							</Grid>
						</Grid>)}

					<TextField type="number" label="Received Amount" value={receivedAmount} onChange={e => {
      if (isNaN(parseFloat(e.target.value))) {
        setReceivedAmount(undefined);
        return;
      } else {
        setReceivedAmount(parseFloat(e.target.value));
      }
    }} sx={{
      mt: 2
    }} fullWidth />

					<Box sx={{
      display: "flex",
      flexDirection: "column",
      mt: 2
    }}>
						<Stack spacing={2} direction="row">
							<DateTimePicker sx={{
          width: "100%"
        }} label="Payment Attachment Received Date" value={receivedDate} onChange={value => setReceivedDate(value)} />
						</Stack>
					</Box>

					<Box sx={{
      display: "flex",
      flexDirection: "column",
      mt: 2
    }}>
						<FormControl fullWidth>
							<InputLabel id="payment-status-label">Payment Status</InputLabel>
							<Select labelId="payment-status-label" id="payment-status-select" value={paymentUploadStatus} label="Payment Status" onChange={e => setPaymentUploadStatus(e.target.value as unknown as PAYMENT_VERIFICATION_UPLOAD_STATUS)}>
								<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STATUS.DEPOSIT_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STATUS.DEPOSIT_PAYMENT}>
									Deposit Payment
								</MenuItem>
								<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STATUS.PARTIAL_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STATUS.PARTIAL_PAYMENT}>
									Partial Payment
								</MenuItem>
								<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STATUS.FINAL_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STATUS.FINAL_PAYMENT}>
									Final Payment
								</MenuItem>
							</Select>
						</FormControl>
					</Box>

					<Box sx={{
      display: "flex",
      my: 2
    }}>
						<Button variant="contained" disabled={!paymentUploadStatus || !receivedAmount} fullWidth onClick={() => {
        if (!paymentUploadStatus || !receivedAmount) return;
        // convert `null` to `undefined` because handleConfirm expects
        // an optional `Date`
        void handleConfirm(paymentUploadStatus, receivedAmount, receivedDate ?? undefined);
        void handleClose();
      }}>
							Upload
						</Button>
					</Box>
					<Box sx={{
      display: "flex",
      mb: 2
    }}>
						<Button variant="outlined" fullWidth onClick={handleClose}>
							Cancel
						</Button>
					</Box>
				</>} data-sentry-element="Dialog" data-sentry-component="PaymentVerificationDialog" data-sentry-source-file="payment-verification-dialog.tsx" />;
};